import React from 'react';
import { PracticeInfo } from '../api/bookingApi';

// `fullBleed` (v2 form flow only): on phones the card chrome disappears and the
// content fills the screen edge-to-edge so the flow can run sticky header/footer
// chrome and a single page scroll; from `sm` up the layout is the classic glass
// card, identical to the default. `overflow-hidden` must not wrap the full-bleed
// phone layout — a sticky child never sticks inside an overflow-hidden ancestor —
// and the blurred mesh circles it exists to clip are hidden there anyway.
export default function Shell({ children, practiceInfo, fullBleed = false }: {
  children: React.ReactNode;
  practiceInfo?: PracticeInfo | null;
  fullBleed?: boolean;
}) {
  return (
    <div
      className={
        fullBleed
          ? 'min-h-screen-dyn relative flex flex-col items-center justify-start py-0 px-0 sm:py-12 md:py-16 sm:px-4 sm:overflow-hidden bg-white'
          : 'min-h-screen relative flex flex-col items-center justify-start py-6 sm:py-12 md:py-16 px-4 overflow-hidden bg-white'
      }
    >
      {/* Animated Mesh Gradient Background */}
      <div className={`absolute inset-0 pointer-events-none opacity-40 ${fullBleed ? 'hidden sm:block' : ''}`}>
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-[#26add0] blur-[120px] rounded-full animate-pulse" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-[#26add0] opacity-30 blur-[120px] rounded-full animate-pulse" style={{ animationDuration: '12s', animationDelay: '2s' }} />
      </div>

      {/* Logo / branding */}
      <div className={`mb-6 sm:mb-10 md:mb-12 text-center relative z-10 ${fullBleed ? 'hidden sm:block' : ''}`}>
        {practiceInfo?.logo_url ? (
          <img
            src={practiceInfo.logo_url}
            alt={practiceInfo.name}
            className="h-14 mx-auto mb-2 object-contain drop-shadow-sm"
          />
        ) : (
          <span className="text-primary-deep font-extrabold text-2xl tracking-tight uppercase">
            {practiceInfo?.name || 'Booking Portal'}
          </span>
        )}
      </div>

      {/* Glass Card (full-bleed column on phones when fullBleed) */}
      <div
        className={
          fullBleed
            ? 'w-full flex-1 flex flex-col relative z-10 sm:flex-none sm:glass-card sm:max-w-lg md:max-w-2xl'
            : 'w-full max-w-lg md:max-w-2xl glass-card relative z-10'
        }
      >
        {children}
      </div>

      <p className={`mt-8 sm:mt-12 text-sm text-gray-500 relative z-10 font-medium tracking-tight ${fullBleed ? 'hidden sm:block' : ''}`}>
        Powered by{' '}
        <a href="https://operaitor.co" className="text-primary-deep hover:text-primary-deeper transition-colors" target="_blank" rel="noreferrer">
          Operaitor
        </a>
      </p>
    </div>
  );
}
